/**
 * `/admin/study` — the study's live view and the manual-review table. Behind
 * the same login as `/admin`; this page has no login form of its own and sends
 * a logged-out visitor there. It computes no pay: the owner pays on Prolific
 * by hand, from the review table.
 *
 * Prolific ids appear here and only here, because this is the admin. The live
 * view shortens them; the review table needs them whole.
 */

import { useCallback, useEffect, useState } from 'react'
import type { StudyAuditEvent, StudyLiveSnapshot } from '@overcooked/shared'
import { prettyMapName } from '../display'
import { ReviewTable } from './ReviewTable'
import type { StudyParticipantsReply } from './studyApi'
import {
  PLACEHOLDER_BANNER,
  WITHDRAW_CONFIRM,
  fetchStudyEvents,
  fetchStudyLive,
  fetchStudyParticipants,
  withdrawStudyParticipant,
} from './studyApi'

const LIVE_POLL_MS = 3000

type Tab = 'live' | 'participants'

/** `1 recording`, `3 recordings`. */
const count = (n: number, noun: string): string => `${n} ${noun}${n === 1 ? '' : 's'}`

const secs = (ms: number): string => {
  const s = Math.max(0, Math.round(ms / 1000))
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`
}

export default function StudyAdminPage() {
  const [tab, setTab] = useState<Tab>('live')
  const [gate, setGate] = useState<'checking' | 'in' | 'out' | 'off'>('checking')
  const [live, setLive] = useState<StudyLiveSnapshot | null>(null)
  const [people, setPeople] = useState<StudyParticipantsReply | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [events, setEvents] = useState<StudyAuditEvent[] | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const readLive = useCallback(async () => {
    const outcome = await fetchStudyLive()
    if (outcome.ok) {
      setLive(outcome.value)
      setGate('in')
      setError(null)
    } else if (outcome.kind === 'unauthorised') setGate('out')
    else if (outcome.kind === 'unconfigured') setGate('off')
    else setError(outcome.error)
  }, [])

  const readPeople = useCallback(async () => {
    const outcome = await fetchStudyParticipants()
    if (outcome.ok) setPeople(outcome.value)
    else setError(outcome.error)
  }, [])

  useEffect(() => {
    void readLive()
    if (tab !== 'live') return
    const timer = setInterval(() => void readLive(), LIVE_POLL_MS)
    return () => clearInterval(timer)
  }, [tab, readLive])

  useEffect(() => {
    if (gate === 'in' && tab !== 'live') void readPeople()
  }, [gate, tab, readPeople])

  const showEvents = async (pid: string): Promise<void> => {
    if (open === pid) {
      setOpen(null)
      return
    }
    setOpen(pid)
    setEvents(null)
    const outcome = await fetchStudyEvents(pid)
    setEvents(outcome.ok ? outcome.value : [])
  }

  const withdraw = async (pid: string): Promise<void> => {
    if (!window.confirm(WITHDRAW_CONFIRM(pid))) return
    setNotice(null)
    const outcome = await withdrawStudyParticipant(pid)
    if (!outcome.ok) {
      setError(outcome.error)
      return
    }
    const { deleted, alreadyWithdrawn } = outcome.value
    setError(null)
    setNotice(
      alreadyWithdrawn
        ? `${pid} was already withdrawn.`
        : `Withdrew ${pid}. Deleted ${count(deleted.audio, 'recording')}, ${count(deleted.surveys, 'survey answer')} and the event trail.`,
    )
    setOpen(null)
    await readPeople()
  }

  const header = (
    <header className="bar">
      <span className="brand">
        Over<em>cooked</em>
      </span>
      <span className="code">Study admin</span>
      <a className="bar-link" href="/admin">
        Admin
      </a>
    </header>
  )

  if (gate === 'checking') {
    return (
      <main className="page">
        {header}
        <p className="hint">Checking whether you are logged in…</p>
      </main>
    )
  }
  if (gate !== 'in') {
    return (
      <main className="page">
        {header}
        <section className="admin-gate">
          <h1>{gate === 'off' ? 'Admin is turned off' : 'Not logged in'}</h1>
          <p className="hint">
            {gate === 'off' ? (
              <>
                Set <code>ADMIN_PASSWORD</code> (12 or more characters) on the server and restart it.
              </>
            ) : (
              <>
                Log in on the <a href="/admin">admin page</a>, then come back here.
              </>
            )}
          </p>
        </section>
      </main>
    )
  }

  const placeholders = live?.placeholderCodes ?? people?.placeholderCodes ?? []

  return (
    <main className="page study-admin">
      {header}
      {placeholders.length > 0 && (
        <p className="error study-banner" role="alert" data-testid="study-placeholder-banner">
          {PLACEHOLDER_BANNER} ({placeholders.join(', ')})
        </p>
      )}
      {live && !live.open && <p className="error">The study is closed: {live.closedReason}</p>}
      {error && <p className="error">{error}</p>}
      {notice && (
        <p className="notice" role="status" data-testid="study-withdraw-notice">
          {notice}
        </p>
      )}

      <nav className="study-tabs" role="tablist">
        {(['live', 'participants'] as const).map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} className={tab === t ? 'ghost study-tab-on' : 'ghost'} onClick={() => setTab(t)}>
            {t === 'live' ? 'Live' : 'Participants'}
          </button>
        ))}
      </nav>

      {tab === 'live' && live && (
        <section className="admin-layout">
          <article className="admin-panel">
            <h2>Waiting for a partner ({live.queue.length})</h2>
            <table className="admin-table">
              <thead>
                <tr><th>Participant</th><th>PID</th><th>Waited</th><th>Connected</th></tr>
              </thead>
              <tbody>
                {live.queue.map((q) => (
                  <tr key={q.participantId}><td>{q.participantId.slice(0, 8)}</td><td>{q.shortPid}</td><td>{secs(q.waitedMs)}</td><td>{q.connected ? 'yes' : 'no'}</td></tr>
                ))}
              </tbody>
            </table>
          </article>
          <article className="admin-panel">
            <h2>On their own ({live.solo.length})</h2>
            <table className="admin-table">
              <thead>
                <tr><th>Participant</th><th>PID</th><th>Phase</th><th>For</th><th>Connected</th></tr>
              </thead>
              <tbody>
                {live.solo.map((s) => (
                  <tr key={s.participantId}><td>{s.participantId.slice(0, 8)}</td><td>{s.shortPid}</td><td>{s.phase}</td><td>{secs(s.elapsedMs)}</td><td>{s.connected ? 'yes' : 'no'}</td></tr>
                ))}
              </tbody>
            </table>
          </article>
          <article className="admin-panel">
            <h2>Pairs ({live.dyads.length})</h2>
            <table className="admin-table">
              <thead>
                <tr><th>Pair</th><th>Game code</th><th>Round</th><th>Kitchen</th><th>State</th><th>Partner left</th></tr>
              </thead>
              <tbody>
                {live.dyads.map((d) => (
                  <tr key={d.studySessionId}><td>{d.studySessionId.slice(0, 8)}</td><td>{d.roomCode}</td><td>{d.gameIndex}</td><td>{prettyMapName(d.mapName)}</td><td>{d.phase}</td><td>{d.partnerLost ? 'yes' : 'no'}</td></tr>
                ))}
              </tbody>
            </table>
          </article>
        </section>
      )}

      {tab === 'participants' && (
        <section className="admin-panel">
          <h2>Participants ({people?.review.length ?? '…'})</h2>
          <p className="hint">Prolific pays stage 4 itself. Pay everyone else by hand for their time. Click a row for its events or to withdraw.</p>
          <ReviewTable
            rows={people?.review ?? []}
            open={open}
            events={events}
            onRow={(pid) => void showEvents(pid)}
            onWithdraw={(pid) => void withdraw(pid)}
          />
        </section>
      )}
    </main>
  )
}
